/**
 * EmployeeDirectoryModule — Unified team directory.
 *
 * Source of truth = Slack workspace (every employee has Slack). Telzio
 * extension/availability is layered in when emails match. Each row offers
 * Slack DM (always) + click-to-call (only when Telzio extension exists).
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Users, Phone, MessageSquare, Search, Loader2 } from 'lucide-react';
import { authFetch } from '../lib/api';

const API = process.env.REACT_APP_BACKEND_URL;
const DEFAULT_VISIBLE = 6;

const Avatar = ({ name, image, size = 32 }) => {
  if (image) {
    return (
      <img
        src={image}
        alt={name || ''}
        className="rounded-full object-cover flex-shrink-0"
        style={{ width: size, height: size }}
        onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling?.classList.remove('hidden'); }}
      />
    );
  }
  const initials = (name || '?').split(' ').map((p) => p[0]).filter(Boolean).slice(0, 2).join('').toUpperCase();
  return (
    <div
      className="rounded-full bg-brand/10 text-brand font-semibold flex items-center justify-center flex-shrink-0"
      style={{ width: size, height: size, fontSize: Math.round(size * 0.38) }}
    >
      {initials}
    </div>
  );
};

const EmployeeDirectoryModule = () => {
  const [members, setMembers] = useState([]);
  const [slackConfigured, setSlackConfigured] = useState(true);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [showAll, setShowAll] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await authFetch(`${API}/api/telzio/directory`);
      if (res.ok) {
        const data = await res.json();
        setMembers(data.members || []);
        setSlackConfigured(data.slack_configured !== false);
      }
    } catch { /* */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return members;
    return members.filter((m) =>
      m.full_name?.toLowerCase().includes(q) ||
      m.email?.toLowerCase().includes(q) ||
      m.title?.toLowerCase().includes(q) ||
      String(m.telzio?.extension || '').includes(q) ||
      (m.telzio?.groups || []).some((g) => g.toLowerCase().includes(q))
    );
  }, [members, query]);

  const visible = showAll ? filtered : filtered.slice(0, DEFAULT_VISIBLE);

  const callExtension = (m) => {
    const ext = m.telzio?.extension;
    if (!ext) return;
    window.dispatchEvent(new CustomEvent('ventas:softphone:call', {
      detail: { e164: String(ext), label: m.full_name },
    }));
  };

  const slackDM = (m, evt) => {
    const slack = m.slack || {};
    if (!slack.user_id) return;
    // Shift+click → open in real Slack instead of in-app composer
    if (evt && evt.shiftKey) {
      const team = slack.team_id || '';
      const url = team
        ? `slack://user?team=${team}&id=${slack.user_id}`
        : `https://slack.com/app_redirect?channel=${slack.user_id}`;
      window.open(url, '_blank', 'noopener,noreferrer');
      return;
    }
    window.dispatchEvent(new CustomEvent('ventas:slack:dm', {
      detail: {
        user_id: slack.user_id,
        team_id: slack.team_id,
        name: m.full_name,
        image: m.image,
      },
    }));
  };

  return (
    <section className="rounded-md border border-border-subtle bg-white shadow-xs" data-testid="dashboard-directory-module">
      <div className="flex items-center justify-between border-b border-border-subtle px-5 py-4">
        <h3 className="text-sm font-semibold text-text-primary flex items-center gap-2">
          <Users size={16} className="text-brand" />
          Team directory
          {filtered.length > 0 && (
            <span className="text-xs font-medium bg-brand/10 text-brand px-2 py-0.5 rounded-full">{filtered.length}</span>
          )}
        </h3>
      </div>

      <div className="p-4 space-y-3">
        <div className="relative">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-text-muted" />
          <input
            type="search"
            placeholder="Search teammates"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="w-full pl-8 pr-3 py-1.5 text-sm border border-border-subtle rounded-md focus:outline-none focus:border-brand bg-surface-page"
            data-testid="dir-module-search"
          />
        </div>

        {loading ? (
          <div className="py-6 text-center"><Loader2 size={18} className="animate-spin text-brand mx-auto" /></div>
        ) : !slackConfigured ? (
          <div className="py-4 px-3 bg-amber-50 border border-amber-200 rounded-md text-xs text-amber-800" data-testid="dir-slack-missing">
            <p className="font-semibold mb-1">Slack not configured on this environment</p>
            <p>Add <code className="bg-amber-100 px-1 rounded">SLACK_BOT_TOKEN</code> to your Emergent deployment Secrets to load the team directory.</p>
          </div>
        ) : visible.length === 0 ? (
          <p className="text-sm text-text-muted py-3 text-center">
            {query ? 'No matches' : 'No teammates found'}
          </p>
        ) : (
          <div className="space-y-1">
            {visible.map((m) => {
              const hasSlack = !!m.slack?.user_id;
              const ext = m.telzio?.extension;
              const status = m.telzio?.availability;
              const subtitle = m.title
                || (ext ? `ext ${ext}${m.telzio?.groups?.[0] ? ` · ${m.telzio.groups[0]}` : ''}` : '')
                || m.email;
              return (
                <div
                  key={m.email || m.slack?.user_id}
                  className="flex items-center gap-3 p-2 rounded-md hover:bg-surface-page transition-colors"
                  data-testid={`dir-module-row-${m.email}`}
                >
                  <div className="relative flex-shrink-0">
                    <Avatar name={m.full_name} image={m.image} />
                    {status === 'online' && (
                      <span className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 bg-success rounded-full ring-2 ring-white" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                      <p className="text-sm font-medium text-text-primary truncate">{m.full_name}</p>
                      {ext && (
                        <span className="text-[9px] font-bold uppercase tracking-wider text-brand bg-brand/10 px-1 py-0.5 rounded">
                          {ext}
                        </span>
                      )}
                    </div>
                    <p className="text-[11px] text-text-tertiary truncate">{subtitle}</p>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    {ext && (
                      <button
                        onClick={() => callExtension(m)}
                        className="w-7 h-7 rounded-md bg-brand/10 hover:bg-brand/20 text-brand flex items-center justify-center transition-colors"
                        title={`Call ext ${ext}`}
                        data-testid={`dir-module-call-${m.email}`}
                      >
                        <Phone size={12} />
                      </button>
                    )}
                    {hasSlack && (
                      <button
                        onClick={(e) => slackDM(m, e)}
                        className="w-7 h-7 rounded-md bg-chart-purple/10 hover:bg-chart-purple/20 text-chart-purple flex items-center justify-center transition-colors"
                        title={`Send Slack DM to ${m.full_name} (Shift+click to open in Slack)`}
                        data-testid={`dir-module-slack-${m.email}`}
                      >
                        <MessageSquare size={12} />
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {filtered.length > DEFAULT_VISIBLE && (
          <button
            onClick={() => setShowAll((s) => !s)}
            className="w-full text-xs font-medium text-brand hover:text-brand-hover py-1"
            data-testid="dir-module-toggle-all"
          >
            {showAll ? 'Show less' : `Show all ${filtered.length}`}
          </button>
        )}
      </div>
    </section>
  );
};

export default EmployeeDirectoryModule;
